import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, Link } from 'react-router-dom';
import { Menu, Bell, Clock, LogOut, Search, User } from 'lucide-react';
import Sidebar from './Sidebar';
import { useAuth } from '../../context/AuthContext';
import { ROLE_META } from '../../utils/roles';
import Spinner, { Modal } from '../common/Spinner';
import api from '../../utils/api';
import SearchInput from '../common/SearchInput';
import SearchResultsDropdown from '../common/SearchResultsDropdown';

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({});
  const [searching, setSearching] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const { user, changeRole, logout } = useAuth();
  const navigate = useNavigate();
  const meta = ROLE_META[user?.role] || { label: user?.role, color: '#6C63FF' };
  const inputRef = useRef(null);

  const [notifications, setNotifications] = useState([]);
  const [notifDropdownOpen, setNotifDropdownOpen] = useState(false);
  const notifRef = useRef(null);

  const formatDate = () => {
    const options = { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' };
    return new Date().toLocaleDateString('en-GB', options);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data.data || res.data || []);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setNotifDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await api.patch('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleNotificationClick = async (notif) => {
    try {
      if (!notif.is_read) {
        await api.patch(`/notifications/${notif.notif_id}/read`);
        setNotifications(prev => prev.map(n => n.notif_id === notif.notif_id ? { ...n, is_read: true } : n));
      }
      setNotifDropdownOpen(false);
      
      if (notif.entity_type === 'leave_request' || notif.entity_type === 'leave') {
        navigate('/leave');
      } else if (notif.entity_type === 'loans' || notif.entity_type === 'loan') {
        navigate('/loans');
      } else if (notif.entity_type === 'performance' || notif.entity_type === 'appraisal') {
        navigate('/performance');
      } else if (notif.entity_type === 'disciplinary' || notif.entity_type === 'grievance') {
        navigate('/disciplinary');
      } else if (notif.entity_type === 'promotions' || notif.entity_type === 'transfer') {
        navigate('/promotions');
      } else if (notif.entity_type === 'training') {
        navigate('/training');
      } else if (notif.entity_type === 'onboarding') {
        navigate('/onboarding');
      } else if (notif.entity_type === 'contracts' || notif.entity_type === 'document') {
        navigate('/documents');
      } else if (notif.entity_type === 'anomalies' || notif.entity_type === 'anomaly') {
        navigate('/anomalies');
      } else if (notif.entity_type === 'employees' || notif.entity_type === 'employee') {
        if (notif.entity_id) {
          navigate(`/employees/${notif.entity_id}`);
        } else {
          navigate('/employees');
        }
      }
    } catch (err) {
      console.error('Failed to handle notification click:', err);
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  useEffect(() => {
    if (searchOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [searchOpen]);

  useEffect(() => {
    setSelectedIndex(-1);
    if (!searchQuery.trim()) {
      setSearchResults({});
      return;
    }
    const delayDebounceFn = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(`/search?q=${encodeURIComponent(searchQuery)}`);
        setSearchResults(res.data.data || res.data || {});
      } catch (err) {
        console.error('Search failed:', err);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  // Flatten results for keyboard indexing
  const flatResults = [];
  Object.entries(searchResults).forEach(([moduleName, items]) => {
    if (items && items.length > 0) {
      items.forEach(item => {
        flatResults.push({ ...item, group: moduleName });
      });
    }
  });

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % Math.max(1, flatResults.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + flatResults.length) % Math.max(1, flatResults.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < flatResults.length) {
        handleResultClick(flatResults[selectedIndex]);
      } else if (searchQuery.trim() && flatResults.length > 0) {
        handleResultClick(flatResults[0]);
      }
    }
  };

  const handleResultClick = (item) => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults({});

    if (item.module === 'employees') {
      navigate(`/employees/${item.id}`);
    } else if (item.module === 'departments') {
      navigate('/system?tab=departments');
    } else if (item.module === 'payroll') {
      navigate('/payroll');
    } else if (item.module === 'attendance') {
      navigate('/attendance');
    } else if (item.module === 'leave') {
      navigate('/leave');
    } else if (item.module === 'training') {
      navigate('/training');
    } else if (item.module === 'grievances') {
      navigate('/grievances');
    } else if (item.module === 'assets') {
      navigate('/assets');
    } else if (item.module === 'clearances') {
      navigate('/clearances');
    } else if (item.module === 'reports') {
      navigate('/reports');
    } else if (item.module === 'configurations') {
      if (item.id === 'roles') {
        navigate('/system?tab=roles');
      } else if (item.id === 'preferences' || item.id === 'approval_levels') {
        navigate('/system?tab=statutory');
      } else {
        navigate(`/system?tab=${item.id}`);
      }
    }
  };

  return (
    <div className="app-layout" style={{ background: '#F8F9FA' }}>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="app-main">
        {/* Topbar */}
        <header className="topbar" style={{
          position: 'sticky', top: 0, zIndex: 20, height: '64px',
          display: 'flex', alignItems: 'center', gap: '20px', padding: '0 24px',
          background: '#FFFFFF', borderBottom: '1px solid var(--border)',
          fontFamily: "'Outfit', sans-serif"
        }}>
          <button
            className="btn btn-icon btn-ghost"
            onClick={() => setSidebarOpen(true)}
            style={{ display: 'none' }} /* shown via media query override */
            aria-label="Open menu"
          >
            <Menu size={18} />
          </button>

          {/* Active session role dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.5px' }}>
              SELECT ACTIVE SESSION ROLE:
            </span>
            <select
              value={user?.role || ''}
              onChange={(e) => changeRole(e.target.value)}
              style={{
                padding: '6px 32px 6px 12px',
                fontSize: '13.5px',
                fontWeight: 600,
                color: 'var(--text-primary)',
                background: '#F3F4F6',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                outline: 'none',
                cursor: 'pointer',
                fontFamily: "'Outfit', sans-serif",
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23565e64\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 10px center',
              }}
            >
              <option value="system_admin">Admin</option>
              <option value="hr_director">HR Director</option>
              <option value="hr_manager">HR Manager</option>
              <option value="hr_officer">HR Officer</option>
              <option value="payroll_officer">Payroll Officer</option>
              <option value="recruitment_officer">Recruitment Officer</option>
              <option value="dept_manager">Department Manager</option>
              <option value="section_supervisor">Section Supervisor</option>
              <option value="employee">Employee</option>
            </select>
          </div>

          {/* Global Search Trigger */}
          <div style={{ position: 'relative', width: '280px', marginLeft: '12px' }}>
            <input
              type="text"
              placeholder="Search anything... (Ctrl+K)"
              readOnly
              onClick={() => setSearchOpen(true)}
              style={{
                width: '100%',
                padding: '8px 12px 8px 34px',
                fontSize: '13.5px',
                background: '#F3F4F6',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                cursor: 'pointer',
                fontFamily: "'Outfit', sans-serif",
                outline: 'none'
              }}
            />
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          </div>

          <div style={{ flex: 1 }} />

          {/* Right section: Date, Notification, Profile, Logout */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            
            {/* Live date/time */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text-secondary)', fontWeight: 500 }}>
              <Clock size={15} style={{ opacity: 0.7 }} />
              <span>{formatDate()}</span>
            </div>

            {/* Notifications */}
            <div ref={notifRef} style={{ position: 'relative' }}>
              <button
                className="btn btn-icon btn-ghost"
                onClick={() => setNotifDropdownOpen(!notifDropdownOpen)}
                style={{
                  position: 'relative', width: 34, height: 34, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: '#F3F4F6', border: '1px solid #E5E7EB',
                  cursor: 'pointer'
                }}
                aria-label="Notifications"
              >
                <Bell size={16} style={{ color: 'var(--text-secondary)' }} />
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: -3, right: -3,
                    width: 16, height: 16, borderRadius: '50%',
                    background: '#DC2626', color: '#FFFFFF', fontSize: '9px', fontWeight: 800,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {unreadCount}
                  </span>
                )}
              </button>

              {notifDropdownOpen && (
                <div style={{
                  position: 'absolute', right: 0, top: '42px',
                  width: '320px', background: '#FFFFFF', borderRadius: '12px',
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #E5E7EB', zIndex: 100, overflow: 'hidden',
                  fontFamily: "'Outfit', sans-serif"
                }}>
                  {/* Header */}
                  <div style={{
                    padding: '12px 16px', borderBottom: '1px solid #F3F4F6',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                  }}>
                    <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)' }}>
                      Notifications
                    </span>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllRead}
                        style={{
                          background: 'none', border: 'none', color: '#3B82F6',
                          fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                          padding: 0
                        }}
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>

                  {/* Notification List */}
                  <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                    {notifications.length === 0 ? (
                      <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
                        No notifications
                      </div>
                    ) : (
                      notifications.map(n => (
                        <div
                          key={n.notif_id}
                          onClick={() => handleNotificationClick(n)}
                          style={{
                            padding: '12px 16px', borderBottom: '1px solid #F9FAFB',
                            cursor: 'pointer', background: n.is_read ? '#FFFFFF' : '#EFF6FF',
                            textAlign: 'left',
                            transition: 'background 0.15s ease'
                          }}
                          onMouseOver={e => e.currentTarget.style.background = '#F3F4F6'}
                          onMouseOut={e => e.currentTarget.style.background = n.is_read ? '#FFFFFF' : '#EFF6FF'}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span style={{
                              fontWeight: n.is_read ? 500 : 700,
                              fontSize: '13px', color: 'var(--text-primary)',
                              lineHeight: '1.3'
                            }}>
                              {n.title}
                            </span>
                            <span style={{ fontSize: '11.5px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                              {n.body}
                            </span>
                            <span style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>
                              {new Date(n.created_at).toLocaleString('en-GB', {
                                day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                              })}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Employee Account Button */}
            <button
              onClick={() => navigate('/ess')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 600,
                padding: '6px 12px',
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                background: '#FFFFFF',
                cursor: 'pointer',
                fontFamily: "'Outfit', sans-serif",
                fontSize: '13px',
                color: 'var(--text-primary)',
                transition: 'all 0.15s'
              }}
              onMouseOver={e => e.currentTarget.style.background = '#F3F4F6'}
              onMouseOut={e => e.currentTarget.style.background = '#FFFFFF'}
            >
              <User size={14} /> Employee Account
            </button>

            {/* Profile Block */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingLeft: '8px', borderLeft: '1px solid var(--border)' }}>
              {/* Yellow User Avatar */}
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: '#F59E0B', display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#FFFFFF', fontWeight: 700, fontSize: '13.5px'
              }}>
                <span style={{ transform: 'translateY(-0.5px)' }}>
                  {user?.full_name?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() || 'U'}
                </span>
              </div>
              
              {/* Name & Title */}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--text-primary)', lineHeight: '1.2' }}>
                  {user?.full_name || 'Nakato Sarah'}
                </span>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 500 }}>
                  {meta.label || 'System Administrator'}
                </span>
              </div>
            </div>

            {/* Logout icon */}
            <button
              onClick={logout}
              className="btn btn-icon btn-ghost"
              style={{ width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}
              title="Sign out"
            >
              <LogOut size={16} />
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="page" style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>
          <Outlet />
        </main>

        {/* Footer */}
        <footer style={{
          padding: '12px 24px', fontSize: 12, color: 'var(--text-muted)',
          borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between',
          background: '#FFFFFF'
        }}>
          <span>© {new Date().getFullYear()} Linqsoft HR v1.0.0</span>
          <Link to="/ess" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>
            Employee Self Service (ESS)
          </Link>
        </footer>
      </div>

      {/* Advanced Global Search Overlay Modal */}
      <Modal open={searchOpen} onClose={() => setSearchOpen(false)} title="Advanced Global Search" subtitle="Press ESC to close" maxWidth={620}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ position: 'relative' }}>
            <SearchInput
              ref={inputRef}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onClear={() => setSearchQuery('')}
              loading={searching}
              placeholder="Search employees, departments, payroll, warnings, assets, reports..."
              onKeyDown={handleKeyDown}
            />
          </div>

          <div style={{ position: 'relative', minHeight: 80 }}>
            <SearchResultsDropdown
              visible={searchOpen}
              results={searchResults}
              query={searchQuery}
              onSelect={handleResultClick}
              selectedIndex={selectedIndex}
              loading={searching}
              onClose={() => setSearchOpen(false)}
              style={{ position: 'static', boxShadow: 'none', border: 'none', padding: 0, marginTop: 4, maxHeight: 340 }}
            />
          </div>
        </div>
      </Modal>

      {/* Mobile toggle — injected via CSS override */}
      <style>{`
        @media(max-width:768px){
          .topbar .btn-ghost[aria-label="Open menu"]{display:flex!important}
          .topbar div[style*="maxWidth"]{max-width:200px!important}
        }
      `}</style>
    </div>
  );
}
